import React, { useState, useMemo } from 'react';
import './FinancialCalendar.css';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const FinancialCalendar = ({ transactions = [], onDayClick, onUpdateTransactionDate, onQuickAdd }) => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(null);

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthName = viewDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const firstDow  = new Date(year, month, 1).getDay();  // 0=Dom
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Agrupa transações por dia do mês exibido
  const byDay = useMemo(() => {
    const map = {};
    transactions.forEach(t => {
      if (!t.date) return;
      const d = new Date(t.date + 'T12:00:00');
      if (d.getFullYear() !== year || d.getMonth() !== month) return;
      const day = d.getDate();
      if (!map[day]) map[day] = { income: 0, expense: 0, items: [] };
      const v = parseFloat(t.value) || 0;
      const isInc = ['receita','entrada'].includes((t.type || '').toLowerCase());
      if (isInc) map[day].income  += v;
      else       map[day].expense += v;
      map[day].items.push(t);
    });
    return map;
  }, [transactions, year, month]);

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const isToday  = (day) => year === today.getFullYear() && month === today.getMonth() && day === today.getDate();

  const selectedItems = selectedDay ? (byDay[selectedDay]?.items || []) : [];
  const selectedData = selectedDay ? byDay[selectedDay] : null;

  const handleDragStart = (e, txId) => {
    e.dataTransfer.setData('txId', txId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetDay) => {
    e.preventDefault();
    const txId = e.dataTransfer.getData('txId');
    if (!txId || !onUpdateTransactionDate) return;
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(targetDay).padStart(2,'0')}`;
    onUpdateTransactionDate(txId, dateStr);
  };

  return (
    <div className="cal-wrapper">
      <div className="cal-header">
        <span className="cal-icon">📅</span>
        <h2 className="cal-title">Calendário Financeiro</h2>
      </div>

      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-month">{monthName}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      <div className="cal-grid">
        {/* Cabeçalho dos dias */}
        {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => (
          <div key={d} className="cal-dow">{d}</div>
        ))}

        {/* Células vazias no início */}
        {Array.from({ length: firstDow }).map((_, i) => (
          <div key={`e${i}`} className="cal-cell empty" />
        ))}

        {/* Dias do mês */}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
          const data    = byDay[day];
          const hasData = !!data;
          const isSelected = selectedDay === day;
          return (
            <div
              key={day}
              className={`cal-cell ${hasData ? 'has-data' : ''} ${isToday(day) ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedDay(isSelected ? null : day)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, day)}
            >
              <span className="cal-day-num">{day}</span>
              {hasData && (
                <div className="cal-dots">
                  {data.income  > 0 && <span className="cal-dot income"  title={`+${fmt(data.income)}`} />}
                  {data.expense > 0 && <span className="cal-dot expense" title={`-${fmt(data.expense)}`} />}
                </div>
              )}
              {hasData && (
                <div className="cal-day-total">
                  {data.income  > 0 && <span className="cal-mini income">+{new Intl.NumberFormat('pt-BR',{notation:'compact',maximumFractionDigits:0}).format(data.income)}</span>}
                  {data.expense > 0 && <span className="cal-mini expense">-{new Intl.NumberFormat('pt-BR',{notation:'compact',maximumFractionDigits:0}).format(data.expense)}</span>}
                </div>
              )}
              {hasData && data.items.some(t => {
                const isInc = ['receita','entrada'].includes((t.type||'').toLowerCase());
                const tStatus = (t.status || '').toLowerCase();
                const isEfetivado = tStatus === 'pago' || tStatus === 'recebido';
                return !isInc && !isEfetivado && t.date < todayStr;
              }) && (
                <span className="cal-overdue-dot" title="Existe lançamento em atraso neste dia">⚠️</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Detalhe do dia selecionado */}
      {selectedDay && (
        <div className="cal-detail">
          <div className="cal-detail-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Lançamentos de {String(selectedDay).padStart(2,'0')}/{String(month+1).padStart(2,'0')}/{year}</span>
            {onDayClick && (
              <button 
                onClick={() => onDayClick(`${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`)}
                style={{ fontSize: '13px', padding: '6px 12px', background: 'var(--btn-primary-bg)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                + Nova Transação
              </button>
            )}
          </div>
          {selectedData && (
            <div className="cal-detail-summary">
              <div className="summary-item"><span className="cal-mini income">Recebido: {fmt(selectedData.income || 0)}</span></div>
              <div className="summary-item"><span className="cal-mini expense">Gasto: {fmt(selectedData.expense || 0)}</span></div>
              <div className="summary-item"><span style={{ fontWeight: 600, fontSize: '13px', color: (selectedData.income - selectedData.expense) >= 0 ? 'var(--success)' : 'var(--danger)' }}>Saldo: {fmt((selectedData.income || 0) - (selectedData.expense || 0))}</span></div>
            </div>
          )}
          {selectedItems.length > 0 ? selectedItems.map(t => {
            const isInc = ['receita','entrada'].includes((t.type||'').toLowerCase());
            const tStatus = (t.status || '').toLowerCase();
            const isEfetivado = tStatus === 'pago' || tStatus === 'recebido';
            const isOverdue = !isInc && (!isEfetivado) && t.date < todayStr;
            return (
              <div 
                key={t.id} 
                className={`cal-detail-item ${isInc ? 'income' : 'expense'}`}
                draggable
                onDragStart={(e) => handleDragStart(e, t.id)}
                title="Pressione e arraste para mudar a data"
                style={{ cursor: 'grab' }}
              >
                <span className="cal-detail-icon">{isInc ? '📈' : '📉'}</span>
                <div className="cal-detail-body">
                  <span className="cal-detail-desc">
                    {t.description || '—'} 
                    {isOverdue && <span title="Em atraso" style={{ marginLeft: 4 }}>⚠️</span>}
                  </span>
                  <span className="cal-detail-cat">
                    {t.category || ''} •{' '}
                    <strong style={{ 
                      textTransform: 'capitalize', 
                      color: isEfetivado ? 'var(--success, #10b981)' : 'var(--warning, #f59e0b)' 
                    }}>
                      {isEfetivado ? '✅ Efetivado' : '⏳ Pendente'}
                    </strong>
                  </span>
                </div>
                <span className={`cal-detail-val ${isInc ? 'income' : 'expense'}`}>
                  {isInc ? '+' : '−'}{fmt(t.value)}
                </span>
              </div>
            );
          }) : (
            <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
              Nenhum lançamento registrado neste dia.
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default FinancialCalendar;
